import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CatalogTenant } from "@/types/tenant-catalog";
import { useSubscriptionPlans } from "@/components/pages/subscriptions/hooks/useSubscriptionPlans";
import {
  useAssignTenantSubscription,
  useCancelTenantSubscription,
  useTenantMergedFeatures,
  useTenantSubscriptions,
} from "../hooks/useTenantSubscriptions";
import { TenantAssignPlanSection } from "./tenant-subscriptions-dialog/TenantAssignPlanSection";
import { TenantMergedFeaturesSection } from "./tenant-subscriptions-dialog/TenantMergedFeaturesSection";
import { TenantSubscriptionsHistorySection } from "./tenant-subscriptions-dialog/TenantSubscriptionsHistorySection";
import {
  currentPlanIds,
  findPlanById,
} from "./tenant-subscriptions-dialog/utils";

interface TenantSubscriptionsDialogProps {
  tenant: CatalogTenant | null;
  onClose: () => void;
}

export const TenantSubscriptionsDialog: React.FC<
  TenantSubscriptionsDialogProps
> = ({ tenant, onClose }) => {
  const { t } = useTranslation();
  const tenantId = tenant?.id ?? null;
  const open = tenant !== null;
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [cancelingKey, setCancelingKey] = useState<string | null>(null);

  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans(
    open,
    { status: "ACTIVE" },
  );
  const { data: subscriptions = [], isLoading: subscriptionsLoading } =
    useTenantSubscriptions(tenantId, open);
  const { data: mergedFeaturesResponse, isLoading: mergedFeaturesLoading } =
    useTenantMergedFeatures(tenantId, open);

  const { mutateAsync: assignPlan, isPending: isAssigning } =
    useAssignTenantSubscription();
  const { mutateAsync: cancelPlan } = useCancelTenantSubscription();

  const assignedPlanIds = useMemo(
    () => currentPlanIds(subscriptions),
    [subscriptions],
  );
  const availablePlans = useMemo(
    () => plans.filter((plan) => !assignedPlanIds.has(plan.id)),
    [assignedPlanIds, plans],
  );
  const selectedPlan = useMemo(
    () => findPlanById(plans, selectedPlanId),
    [plans, selectedPlanId],
  );

  const mergedFeatures = mergedFeaturesResponse?.features ?? [];

  const handleClose = () => {
    setSelectedPlanId("");
    setCancelingKey(null);
    onClose();
  };

  const handleAssign = async () => {
    if (tenantId === null || selectedPlanId.trim() === "") {
      toast.error(t("subscriptions.tenantManagement.assign.validation.plan"));
      return;
    }

    try {
      await assignPlan({ tenantId, planId: selectedPlanId });
      toast.success(t("subscriptions.tenantManagement.assign.successToast"));
      setSelectedPlanId("");
    } catch {
      toast.error(t("subscriptions.tenantManagement.assign.errorToast"));
    }
  };

  const handleCancel = async (
    subscriptionId: string,
    cancelAtPeriodEnd: boolean,
  ) => {
    if (tenantId === null) {
      return;
    }

    const modeKey = cancelAtPeriodEnd ? "period" : "now";
    setCancelingKey(`${subscriptionId}:${modeKey}`);

    try {
      await cancelPlan({ tenantId, subscriptionId, cancelAtPeriodEnd });
      toast.success(
        cancelAtPeriodEnd
          ? t("subscriptions.tenantManagement.cancel.successPeriodToast")
          : t("subscriptions.tenantManagement.cancel.successImmediateToast"),
      );
    } catch {
      toast.error(t("subscriptions.tenantManagement.cancel.errorToast"));
    } finally {
      setCancelingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t("subscriptions.tenantManagement.title", {
              tenantName: tenant?.name ?? "—",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("subscriptions.tenantManagement.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <section className="space-y-4">
            <TenantAssignPlanSection
              availablePlans={availablePlans}
              isAssigning={isAssigning}
              plansLoading={plansLoading}
              selectedPlan={selectedPlan}
              selectedPlanId={selectedPlanId}
              onAssign={() => void handleAssign()}
              onSelectedPlanIdChange={setSelectedPlanId}
            />

            <TenantSubscriptionsHistorySection
              cancelingKey={cancelingKey}
              plans={plans}
              subscriptions={subscriptions}
              subscriptionsLoading={subscriptionsLoading}
              onCancel={(subscriptionId, cancelAtPeriodEnd) => {
                void handleCancel(subscriptionId, cancelAtPeriodEnd);
              }}
            />
          </section>

          <TenantMergedFeaturesSection
            isLoading={mergedFeaturesLoading}
            mergedFeatures={mergedFeatures}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            {t("subscriptions.tenantManagement.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
